import React, { useEffect, useCallback, useState, useRef } from "react"
import { connect } from "react-redux"
import Mousetrap from "mousetrap"
import { type MutationFunction, type MutationResult, useMutation, useQuery } from "@apollo/client/react"
import { actions } from "@/signomatic/store/actions"
import cn from "classnames"
import { isTablet, basketAddToGA4 } from "@/common/util"
import LoadingAnimation from "@/common/components/loading"
import { useTranslation } from "react-i18next"
import type Sign from "@/signomatic/app/sign"
import type { State } from "@/signomatic/store/reducers"
import { ArrowRightLarge, CartFill, MinusLarge, PlusLarge } from "@/skylticons"
import type { EditingState } from "@/common/client_data"
import tailwindTheme from "@/common/styles/theme.module.css"
import Price from "@/signomatic/components/price"
import { useSentry } from "@/common/sentry"
import useMedia from "@/common/hooks/use_media"
import { graphql } from "@/api"
import { Mutation } from "@apollo/client/react/components"
import type { BasketAddMutation, BasketAddMutationVariables } from "@/api/graphql"
import { setBasketStateDoc } from "@/menu/components/basket_modal"
import ClientData from "@/common/client_data"

const basketStatusDoc = graphql(`
  query SubmitButtonBasketStatus {
    basketStatus {
      id

      basketItems {
        totalCount
        ...BasketItem_OrderItem
      }
    }
  }
`)

const basketAddDoc = graphql(`
  mutation BasketAdd($product: String!) {
    basketAdd(product: $product) {
      success
      landingUrl

      item {
        id
        priceSek
        totalCount
        sizeDisplay
        hangType
        materialNameForGtm
        signTypeKey
        exampleId
        discountPercentage
      }

      status {
        ...BasketModal_Order
      }
    }
  }
`)

const basketUpdateDoc = graphql(`
  mutation BasketUpdate($type: String!, $id: Int!, $code: String!, $description: String!, $admin: Boolean!) {
    basketUpdate(type: $type, id: $id, code: $code, description: $description, admin: $admin) {
      location
      error
    }
  }
`)

interface AddButtonProps {
  basketLoading?: boolean
  basketNotEmpty?: boolean
  sign: Sign
  setToolbox: typeof actions.setToolbox
  mutation: MutationFunction<BasketAddMutation, BasketAddMutationVariables>
  result: MutationResult<BasketAddMutation>
  basketItemsCount: number
  showResetTooltip: () => void
}

const AddButton: React.FC<AddButtonProps> = ({
  basketNotEmpty,
  basketLoading,
  result,
  sign,
  mutation,
  setToolbox,
  basketItemsCount,
  showResetTooltip,
}) => {
  const { t } = useTranslation()
  const [count, setCount] = useState(ClientData.signCount || 1)
  const warningShown = useRef(false)
  const oldType = useRef(sign.signType)

  const handleClick = async () => {
    if (oldType.current !== sign.signType) {
      oldType.current = sign.signType
      warningShown.current = false
    }

    if (!sign.state || !sign.state.fixmethod) {
      alert(t("toolbox.fix_method_required"))
      setToolbox("fixmethod")
      return
    }

    if (sign.state.doubleSided && !sign.state.seenBside) {
      alert(t("toolbox.b_side_not_seen"))
      sign.setSide(1)
      return
    }

    if (sign.blankSign) {
      alert(t("toolbox.empty_sign_alert"))
      return
    }

    if (sign.state.objects.length === 0 && !warningShown.current && !sign.objectsDisabled) {
      warningShown.current = true
      if (!confirm(t("toolbox.empty_sign"))) {
        return
      }
    }

    sign.deletePlaceholderTextObjects()
    const code = await sign.signcode()

    if (!code) {
      throw new Error("signcode is null")
    }

    if (count > 1) {
      code.count = count
    }

    await mutation({ variables: { product: JSON.stringify(code) } })
    sign.changesCommited()
    setCount(1)
    sign.removePreviousSignData()
    showResetTooltip()
  }

  const countOp = useCallback(
    (op: string) => {
      const current = count || 0
      const newCount = Math.min(op === "plus" ? current + 1 : current - 1, 999999999)
      setCount(newCount)
    },
    [count, setCount]
  )

  useEffect(() => {
    Mousetrap.bind("f8", () => {
      handleClick()
    })

    return () => {
      Mousetrap.unbind("f8")
    }
  })

  const loading = basketLoading || result.loading

  const CountBox = (
    <div className="count mr-3 flex items-center">
      <button
        className="spinners spinner_minus"
        disabled={count < 2}
        onClick={() => countOp("minus")}
        data-gtm="decrease-quantity"
        type="button"
      >
        <MinusLarge />
      </button>
      <input
        type="text"
        name="basket_item_count"
        className="article_count paragraph-sm mb-0"
        value={count === 0 ? "" : count}
        onChange={e => {
          const newValue = e.target.value
          if (newValue !== "") {
            setCount(Math.min(isNaN(parseInt(newValue)) ? count : parseInt(newValue), 999999999))
          } else {
            setCount(0)
          }
        }}
        onBlur={e => {
          if (e.target.value === "") {
            setCount(1)
          }
        }}
      />

      <button
        className="spinners spinner_plus"
        onClick={() => countOp("plus")}
        data-gtm="increase-quantity"
        type="button"
      >
        <PlusLarge />
      </button>
    </div>
  )

  const desktop = useMedia(`(min-width: ${tailwindTheme.screensLg})`)
  const desktopXl = useMedia(`(min-width: ${tailwindTheme.screensXl})`)
  const desktopLgMax = useMedia(`(max-width: ${tailwindTheme.screensLgmax})`)

  return desktop ? (
    <div className="submit-container">
      <div className="left-col">
        <Price sign={sign} numberOfSigns={basketItemsCount} currentCount={count} />
      </div>
      <div className="add-buttons right-col">
        {CountBox}

        <button
          id="add_to_cart"
          className="button button-emphasis button-medium add-button mb-0 mr-3"
          onClick={handleClick}
          disabled={loading}
          data-gtm="add-to-cart-button"
          type="button"
        >
          {loading ? (
            <LoadingAnimation />
          ) : (
            <span>
              <CartFill /> {t("signomatic.add_to_basket")}
            </span>
          )}
        </button>
        <a
          id="background_order_button"
          className={cn("button button-primary button-medium icon-right checkout !m-0", {
            "pointer-events-none opacity-50": !basketNotEmpty,
          })}
          href={t("route.order")}
          data-gtm="checkout-button"
        >
          <span>
            {desktopLgMax && t("toolbox.checkout")}
            {desktopXl && t("toolbox.checkout_button")}
            <ArrowRightLarge />
          </span>
        </a>
      </div>
    </div>
  ) : (
    <div className="add-buttons mobile">
      <button
        id="add_to_cart"
        className="button button-emphasis button-medium flat squared add-button z-10"
        onClick={handleClick}
        disabled={loading}
        data-gtm="add-to-cart-button"
        type="button"
      >
        {loading ? (
          <LoadingAnimation />
        ) : (
          <span>
            <CartFill /> {t("signomatic.add_to_basket")}
          </span>
        )}
      </button>

      {basketNotEmpty && (
        <a
          id="background_order_button"
          className="button button-primary button-medium flat squared icon-right checkout z-10"
          href={t("route.order")}
          data-gtm="checkout-button"
        >
          <span>
            {t("toolbox.checkout")}
            <ArrowRightLarge />
          </span>
        </a>
      )}
    </div>
  )
}

interface UpdateButtonProps {
  sign: Sign
  editing: EditingState
}

const UpdateButton: React.FC<UpdateButtonProps> = ({ sign, editing: { type, id, admin } }) => {
  const { t } = useTranslation()
  const Sentry = useSentry()
  const [mutationActive, setMutationActive] = useState(false)

  const [mutation, result] = useMutation(basketUpdateDoc, {
    update: (_, { data }) => {
      if (data?.basketUpdate?.error) {
        setMutationActive(false)
        return alert(data.basketUpdate.error)
      }

      if (data?.basketUpdate?.location) {
        sign.updateState({ thickness: undefined }, false)
        sign.storeImmediate()
        sign.changesCommited()
        window.location.href = data.basketUpdate.location
      }
    },
  })

  const handler = useCallback(async () => {
    if (!sign.state.fixmethod) {
      alert(t("toolbox.fix_method_required"))
      return
    }

    setMutationActive(true)

    try {
      const code = await sign.signcode()

      if (!code) {
        setMutationActive(false)
        throw new Error("signcode is null")
      }

      mutation({
        variables: {
          type,
          id,
          code: JSON.stringify(code),
          description: code.description,
          admin,
        },
      })
    } catch (e) {
      Sentry.captureException(e)
      console.error(e)
    }
  }, [admin, sign, type, id, mutation, setMutationActive, t, Sentry])

  const loading = result.loading || mutationActive

  return (
    <div className="update-price">
      {!isTablet() && (
        <div className="price">
          <Price sign={sign} />
        </div>
      )}
      <button
        id="update_sign"
        className={cn("button button-emphasis button-medium add-button", { "full-width": isTablet() })}
        onClick={handler}
        disabled={loading}
        type="button"
      >
        {loading ? <LoadingAnimation /> : t("misc.update_sign")}
      </button>
    </div>
  )
}

interface SubmitButtonOwnProps {
  sign: Sign
  showResetTooltip: () => void
}

interface SubmitButtonStateProps {
  editing: EditingState
}

interface SubmitButtonDispatchProps {
  setToolbox: typeof actions.setToolbox
}

type SubmitButtonProps = SubmitButtonStateProps & SubmitButtonDispatchProps & SubmitButtonOwnProps

const SubmitButton: React.FC<SubmitButtonProps> = ({ sign, editing, setToolbox, showResetTooltip }) => {
  const [setBasketState] = useMutation(setBasketStateDoc)
  const statusResult = useQuery(basketStatusDoc)

  const openBasket = useCallback(() => {
    setBasketState({ variables: { open: true, addedNotice: true } })
  }, [setBasketState])

  return (
    <div className="submit-row">
      {editing && editing.id ? (
        <React.Fragment>
          <UpdateButton sign={sign} editing={editing} />
        </React.Fragment>
      ) : (
        <Mutation<BasketAddMutation, BasketAddMutationVariables>
          mutation={basketAddDoc}
          update={(store, { data: basketAddData }) => {
            if (basketAddData?.basketAdd?.success && basketAddData.basketAdd.item) {
              const orderItem = basketAddData.basketAdd.item
              const id = orderItem.exampleId?.toString() || null

              basketAddToGA4(
                id,
                orderItem.priceSek,
                orderItem.totalCount,
                orderItem.discountPercentage,
                orderItem.signTypeKey,
                orderItem.sizeDisplay,
                orderItem.hangType,
                orderItem.materialNameForGtm,
                basketAddData.basketAdd.landingUrl
              )
              openBasket()
            }
          }}
        >
          {(mutation, result) => (
            <AddButton
              sign={sign}
              basketLoading={statusResult.loading}
              setToolbox={setToolbox}
              mutation={mutation}
              result={result}
              basketNotEmpty={(statusResult.data?.basketStatus?.basketItems || []).length > 0}
              basketItemsCount={
                statusResult.data?.basketStatus?.basketItems?.reduce((a, b) => a + b.totalCount, 0) || 0
              }
              showResetTooltip={showResetTooltip}
            />
          )}
        </Mutation>
      )}
    </div>
  )
}

export default connect(
  ({ editing }: State) => ({
    editing,
  }),
  {
    setToolbox: actions.setToolbox,
  }
)(SubmitButton)
